import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Globe,
  Users,
  Award,
  BookOpen,
  MessageSquare,
  Edit,
  Subtitles,
  Building,
  Phone,
  Mail,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

// Custom hook for animated counter
const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted) {
            setHasStarted(true);
            let startTime = null;
            const animate = (currentTime) => {
              if (!startTime) startTime = currentTime;
              const progress = Math.min((currentTime - startTime) / duration, 1);
              // Easing function for smooth animation
              const easeOutQuart = 1 - Math.pow(1 - progress, 4);
              const currentCount = Math.floor(easeOutQuart * end);
              setCount(currentCount);
              if (progress < 1) {
                requestAnimationFrame(animate);
              } else {
                setCount(end);
              }
            };
            requestAnimationFrame(animate);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [end, duration, hasStarted]);

  return [count, countRef];
};

export default function HomePage() {
  // Counter functionality (from HEAD/main branch)
  const languagesScrollRef = useRef(null);
  const scrollPositionRef = useRef(0);
  
  // Animated counters
  const [studentsCount, studentsRef] = useCounter(1000, 2000);
  const [coursesCount, coursesRef] = useCounter(5, 1500);
  const [countriesCount, countriesRef] = useCounter(3, 1500);
  
  // Sync manual offset with animation cycle to maintain seamless infinite scroll
  const handleAnimationIteration = () => {
    if (languagesScrollRef.current) {
      const scrollElement = languagesScrollRef.current.querySelector('.languages-scroll');
      if (scrollElement) {
        // When animation completes one cycle and resets, adjust manual offset to maintain visual continuity
        // The animation goes from 0 to -33.333%, then resets to 0
        // If we have a manual offset, we need to keep it within bounds
        if (scrollPositionRef.current < -oneSetWidth) {
          // If offset is beyond one set, wrap it back
          scrollPositionRef.current = scrollPositionRef.current + oneSetWidth;
        } else if (scrollPositionRef.current > 0) {
          // If offset is positive, wrap it to negative
          scrollPositionRef.current = scrollPositionRef.current - oneSetWidth;
        }
        // Update the CSS property
        scrollElement.style.setProperty('--manual-offset', `${scrollPositionRef.current}px`);
      }
    }
  };

  // Carousel responsiveness (from mobileresponsiveness branch)
  const [serviceIndex, setServiceIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(1);

  useEffect(() => {
    const updateItemsPerView = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerView(3);
      } else if (window.innerWidth >= 640) {
        setItemsPerView(2);
      } else {
        setItemsPerView(1);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);
    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const features = [
    {
      image: "/expert_coded.png",
      title: "Expert-Certified Trainers",
      description: "Learn from industry professionals with proven expertise",
    },
    {
      image: "/Business_focused_learning.png",
      title: "Business-Focused Learning",
      description: "Curriculum designed for real-world workplace communication",
    },
    {
      image: "/live_interective_class.png",
      title: "Live & Interactive Classes",
      description: "Engaging sessions with real-time feedback and practice",
    },
    {
      image: "/global_language_services.png",
      title: "Global Language Services",
      description: "Comprehensive solutions for individuals & businesses",
    },
  ];

  const languages = [
    { name: "German", levels: "A1-C1", flag: "https://flagcdn.com/w320/de.png", id: "german" },
    { name: "Spanish", levels: "A1-C1", flag: "https://flagcdn.com/w320/es.png", id: "spanish" },
    { name: "Japanese", levels: "N5-N1", flag: "https://flagcdn.com/w320/jp.png", id: "japanese" },
    { name: "Korean", levels: "TOPIK 1-6", flag: "https://flagcdn.com/w320/kr.png", id: "korean" },
    { name: "French", levels: "A1-C1", flag: "https://flagcdn.com/w320/fr.png", id: "french" },
  ];
  
  // Calculate card dimensions for scroll calculations (after languages is defined)
  const cardWidth = 220; // card width
  const gap = 24; // gap between cards
  const cardWithGap = cardWidth + gap;
  const oneSetWidth = languages.length * cardWithGap;

  const services = [
    {
      title: "Professional Interpretation",
      description:
        "On-site & remote interpretation for business, legal, medical, corporate & diplomatic needs",
      icon: <MessageSquare style={{ width: '1.5rem', height: '1.5rem' }} />,
      id: "professional-interpretation",
      image: "/ProfessionalInterpretation.svg",
      imageFit: "cover",
    },
    {
      title: "Translation Services",
      description:
        "Technical, legal, business, academic, website localization - Fast & confidential",
      icon: <BookOpen style={{ width: '1.5rem', height: '1.5rem' }} />,
      id: "translation-services",
      image: "/TranslationServices.png",
      imageFit: "contain",
    },
    {
      title: "Editing & Content Moderation",
      description:
        "Professional proofreading and polishing of translated documents. Multilingual content moderation for accuracy and brand consistency",
      icon: <Edit style={{ width: '1.5rem', height: '1.5rem' }} />,
      id: "editing-content-moderation",
      image: "/ContentModeration.webp",
      imageFit: "cover",
    },
    {
      title: "Transcription & Subtitling",
      description:
        "Professional subtitle creation for videos, webinars, documentaries, and corporate training materials. Accurate transcription in multiple languages",
      icon: <Subtitles style={{ width: '1.5rem', height: '1.5rem' }} />,
      id: "transcription-subtitling",
      image: "/TranscriptionSubtitling.jpeg",
      imageFit: "contain",
    },
    {
      title: "B2B Language Solutions",
      description:
        "Corporate language training, business interpretation, translation & localization",
      icon: <Building style={{ width: '1.5rem', height: '1.5rem' }} />,
      id: "b2b-language-solutions",
      image: "/b2b.png",
      imageFit: "cover",
    },
  ];

  return (
    <div className="home-page">
      <Header />

      {/* Hero Section */}
      <section className="hero-banner">
        <div className="hero-content-wrapper">
          <div className="hero-text-content">
            <h1>
              University of <span className="text-accent">Languages</span>
            </h1>
            <h2>Unlocking Global Communication</h2>
            <p>
              Master languages that open doors to global opportunities.<br />
              Learn from expert trainers with business-focused<br />
              curriculum designed for real-world success.
            </p>
            <div className="hero-buttons">
              <Link to="/courses" className="btn-hero btn-primary">
                Find Courses →
              </Link>
              <Link to="/contact" className="btn-hero btn-secondary">
                Try Demo Class →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <div className="section-content">
          <div className="statistics-grid">
            <div className="stat-card" ref={studentsRef}>
              <div className="stat-number">{studentsCount.toLocaleString()}+</div>
              <div className="stat-label">Students Enrolled</div>
            </div>
            <div className="stat-card" ref={coursesRef}>
              <div className="stat-number">{coursesCount}+</div>
              <div className="stat-label">Courses</div>
            </div>
            <div className="stat-card" ref={countriesRef}>
              <div className="stat-number">{countriesCount}+</div>
              <div className="stat-label">Students from <span className="stat-highlight"></span> countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Languages */}
      <section className="languages-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Featured Languages</h2>
            <p>Master the languages that matter in today's global economy</p>
          </div>
          <div className="languages-wrapper">
            <button 
              className="scroll-arrow scroll-arrow-left"
              onClick={() => {
                if (languagesScrollRef.current) {
                  const scrollElement = languagesScrollRef.current.querySelector('.languages-scroll');
                  if (scrollElement) {
                    // Update manual offset (move left)
                    scrollPositionRef.current -= cardWithGap;
                    
                    // Wrap around: keep offset within one set range for seamless loop
                    // When we go past -oneSetWidth, wrap to continue from duplicate set
                    while (scrollPositionRef.current <= -oneSetWidth) {
                      scrollPositionRef.current += oneSetWidth;
                    }
                    
                    // Update CSS custom property
                    scrollElement.style.setProperty('--manual-offset', `${scrollPositionRef.current}px`);
                  }
                }
              }}
              aria-label="Scroll left"
            >
              <ChevronLeft style={{ width: '1.75rem', height: '1.75rem' }} />
            </button>
            <div className="languages-scroll-container" ref={languagesScrollRef}>
              <div 
                className="languages-scroll"
                onAnimationIteration={handleAnimationIteration}
              >
                {/* First set */}
                {languages.map((lang, index) => (
                  <div key={`set1-${index}`} className="card card-language">
                    <div className="flag-container">
                      <img src={lang.flag} alt={`${lang.name} flag`} className="flag-image" />
                    </div>
                    <div className="card-content">
                      <div className="card-text-content">
                        <h3>{lang.name}</h3>
                        <p>{lang.levels}</p>
                      </div>
                      <Link to={`/courses#${lang.id}`} className="link-primary">
                        Learn More →
                      </Link>
                    </div>
                  </div>
                ))}
                {/* Second set - duplicate for seamless loop */}
                {languages.map((lang, index) => (
                  <div key={`set2-${index}`} className="card card-language">
                    <div className="flag-container">
                      <img src={lang.flag} alt={`${lang.name} flag`} className="flag-image" />
                    </div>
                    <div className="card-content">
                      <div className="card-text-content">
                        <h3>{lang.name}</h3>
                        <p>{lang.levels}</p>
                      </div>
                      <Link to={`/courses#${lang.id}`} className="link-primary">
                        Learn More →
                      </Link>
                    </div>
                  </div>
                ))}
                {/* Third set - extra duplicate to prevent white space */}
                {languages.map((lang, index) => (
                  <div key={`set3-${index}`} className="card card-language">
                    <div className="flag-container">
                      <img src={lang.flag} alt={`${lang.name} flag`} className="flag-image" />
                    </div>
                    <div className="card-content">
                      <div className="card-text-content">
                        <h3>{lang.name}</h3>
                        <p>{lang.levels}</p>
                      </div>
                      <Link to={`/courses#${lang.id}`} className="link-primary">
                        Learn More →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button 
              className="scroll-arrow scroll-arrow-right"
              onClick={() => {
                if (languagesScrollRef.current) {
                  const scrollElement = languagesScrollRef.current.querySelector('.languages-scroll');
                  if (scrollElement) {
                    // Update manual offset (move right)
                    scrollPositionRef.current += cardWithGap;
                    
                    // Wrap around: keep offset within one set range for seamless loop
                    // When we go past 0, wrap to continue from previous set
                    while (scrollPositionRef.current >= 0) {
                      scrollPositionRef.current -= oneSetWidth;
                    }
                    
                    // Update CSS custom property
                    scrollElement.style.setProperty('--manual-offset', `${scrollPositionRef.current}px`);
                  }
                }
              }}
              aria-label="Scroll right"
            >
              <ChevronRight style={{ width: '1.75rem', height: '1.75rem' }} />
            </button>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive language solutions for individuals and businesses</p>
          </div>
          <div className="services-carousel-wrapper">
            <button
              className="carousel-nav-btn carousel-nav-left"
              onClick={() => setServiceIndex(Math.max(0, serviceIndex - 1))}
              disabled={serviceIndex === 0}
              aria-label="Previous services"
            >
              <ChevronLeft className="carousel-icon" />
            </button>
            <div className="services-carousel-container">
              <div
                className="services-carousel-track"
                style={{
                  transform: `translateX(-${serviceIndex * (100 / itemsPerView)}%)`,
                }}
              >
                {services.map((service, index) => (
                  <div key={index} className="card card-service">
                    <div className="service-header">
                      <div className="icon-wrapper service-icon">{service.icon}</div>
                      <h3>{service.title}</h3>
                    </div>
                    <p>{service.description}</p>
                    <div className="service-footer">
                      <Link to={`/services#${service.id}`} className="link-primary">
                        Learn More →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              className="carousel-nav-btn carousel-nav-right"
              onClick={() => setServiceIndex(Math.min(serviceIndex + 1, services.length - itemsPerView))}
              disabled={serviceIndex >= services.length - itemsPerView}
              aria-label="Next services"
            >
              <ChevronRight className="carousel-icon" />
            </button>
          </div>
          <div className="section-cta">
            <Link to="/services" className="btn btn-primary btn-lg">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="features-section">
        <div className="section-content">
          <div className="section-header">
            <h2>Why Choose University of Languages?</h2>
            <p>Excellence in language education and global communication</p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="icon-wrapper">
                  <div className="icon-bg">
                    <img 
                      src={feature.image} 
                      alt={feature.title}
                      className="feature-icon-image"
                    />
                  </div>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-content">
          <h2>Ready to Start Your Language Journey?</h2>
          <p>
            Join thousands of students who have achieved fluency with our expert
            training
          </p>
          <div className="cta-contact-info">
            <div className="cta-phone">
              <Phone style={{ width: '1.5rem', height: '1.5rem' }} />
              <span>+91 82172 34825</span>
            </div>
            <div className="cta-email">
              <Mail style={{ width: '1.5rem', height: '1.5rem' }} />
              <span>info@universityoflanguages.com</span>
            </div>
          </div>
          <div className="cta-buttons">
            <Link to="/courses" className="btn-cta btn-cta-primary">
              Enroll Now
            </Link>
            <Link to="/contact#contact-form" className="btn-cta btn-cta-outline">
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}